import { Link } from '@reach/router';
import styled, { css } from 'styled-components';
import { withQuery } from '../data/withQuery';
import { Metafields, Product } from '@/types/ecommerce.types';
import { getMetafieldV2 } from '@/utils/utils';
import { ProductOptionsTitle } from './ProductOptions.styles';
import { colors } from '@/themes/colorsMapping';

const StyledLink = styled(Link)<{ $availableToSell?: boolean }>`
  ${(props) =>
    !props.$availableToSell &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
    `}
  span {
    display: inline-block;
    font-size: 16px;
    line-height: 16px;
    font-weight: 500;
    text-align: center;
    width: 100%;
  }
`;
StyledLink.displayName = 'ColorSwatch';

const SwatchesList = styled.ul`
  padding: 3px;
  margin-top: 13px;
  position: relative;
  display: grid;
  list-style-type: none;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  gap: 5px;

  @media (min-width: 800px) {
    margin-top: 10px;
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  }

  li {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid ${colors.darkGray};
    &.tmplt-product__connected-collection-item--current {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px var(--color-primary-20);

      span {
        color: ${colors.purple} !important;
        &.detail {
          opacity: 0.5;
      }
    }
  }
  a {
    background-color: transparent !important;
    background: none !important;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 10px;

    span {

      &.detail {
        color: ${colors.darkestGray};
        font-weight: 400;
        font-size: 14px;
      }

      @media (max-width: 800px) {
        display: block;
      }

    }
  }
  img {
    width: 100%;
    height: 70px;
    object-position: bottom;
    object-fit: cover;
  }
  .placeholder-img {
    height: 70px;
    width: 100%;
    background-color: #f1f0ec;
    background: linear-gradient(to right, #f1f0ec 0%, #ffffff 50%, #f1f0ec 100%);
    background-size: 800px 100%;
    animation: shimmer 1.5s infinite linear;
  }
  @keyframes shimmer {
    0% {
      background-position: -800px 0;
    }
    100% {
      background-position: 800px 0;
    }
  }
`;

export type Color = {
  title: string;
  handle: string;
  color: string;
  colorName: string;
  availableToSell: boolean;
};

type PropTypes = {
  product: Product;
  className: string;
  pagePath?: string;
  showDefaultColor?: boolean;
  openStyle?: boolean;
  sectionTitle?: string;
  shopMetafields: Metafields;
};

const ProductGallerySizes = ({
  product,
  className,
  pagePath = '/products',
  sectionTitle,
}: PropTypes) => {
  interface productItem {
    availableForSale: boolean;
    colorCode: string;
    currentProduct: boolean;
    galleryDims: string;
    itemCount: string;
    productHandle: string;
  }

  const groupedSizes = product?.gallery_sizes_group?.reference;
  const currentVarColorCode = getMetafieldV2('color', product?.metafields);
  const currentGalleryWidth = getMetafieldV2('gallery_width_in', product?.metafields);
  const currentGalleryHeight = getMetafieldV2('gallery_height_in', product?.metafields);
  const currentGalleryDims = `${currentGalleryWidth}" x ${currentGalleryHeight}"`;

  const productItems: productItem[] =
    groupedSizes?.products?.nodes?.map((item: any) => {
      const colorCode = item?.color_code?.value;
      const productHandle = item?.handle;
      const galleryDims = `${item?.gallery_width_in.value}" x ${item?.gallery_height_in.value}"`;
      let currentProduct = false;

      const productItems = item?.variants?.nodes[0]?.selectedOptions;
      const productItemsSize = productItems.find((u: any) => u.name === 'Size');

      let itemCount = '';

      if (product?.handle.toLowerCase() === item?.handle.toLowerCase()) {
        currentProduct = true;
      }

      if (productItemsSize) {
        const itemCountText = productItemsSize?.value.toLowerCase();

        if (itemCountText.includes('framed pictures')) {
          itemCount = itemCountText.replace('framed pictures', 'frames');
        } else if (itemCountText.includes('premium canvas prints')) {
          itemCount = itemCountText.replace('premium canvas prints', 'canvas');
        } else if (itemCountText.includes('hd metal prints')) {
          itemCount = itemCountText.replace('hd metal prints', 'metal');
        } else {
          itemCount = itemCountText;
        }
      }

      return {
        availableForSale: item?.variants?.nodes[0]?.availableForSale,
        colorCode: colorCode.toLowerCase(),
        currentProduct: currentProduct,
        galleryDims: galleryDims,
        itemCount: itemCount,
        productHandle: productHandle,
      };
    }) || [];

  if (!product || !groupedSizes) {
    return null;
  }

  return (
    <div className={className}>
      {sectionTitle && (
        <ProductOptionsTitle>
          {sectionTitle}: {'\u00A0'}
          <span>{currentGalleryDims ?? currentGalleryDims}</span>
        </ProductOptionsTitle>
      )}
      <SwatchesList>
        {productItems?.map((item, index) => {
          return currentVarColorCode?.toLowerCase() === item?.colorCode ? (
            <li
              key={index}
              className={`product-option-color-item ${
                item?.currentProduct ? 'tmplt-product__connected-collection-item--current' : ''
              }`}
            >
              <StyledLink
                $availableToSell={item?.availableForSale}
                to={!item?.currentProduct ? `${pagePath}/${item?.productHandle}` : ''}
                title={`${item?.galleryDims} • ${item?.itemCount}`}
              >
                <span className={'label'}>{item?.galleryDims}</span>
                <span className={'detail'}>{item?.itemCount} </span>
              </StyledLink>
            </li>
          ) : null;
        })}
      </SwatchesList>
    </div>
  );
};

ProductGallerySizes.displayName = 'ProductGallerySizes';
export default withQuery(ProductGallerySizes);
